/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ChevronLeftIcon,
  DashboardCardsSkeleton,
  DataState,
  DataStateError,
  DETAIL_ACTION_PRIMARY,
  DETAIL_ACTION_SECONDARY,
  SectionCard,
  Text,
  Title,
  UnstyledButton,
} from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { EntityDocumentsPanel } from '@/features/sales/components/EntityDocumentsPanel';
import { LeaseVistoriesPanel } from '@/features/vistories/components/LeaseVistoriesPanel';
import { useVistoryData } from '@/features/vistories/hooks/useVistoryData';
import {
  BUILT_IN_DOCUMENT_CATEGORY_IDS,
  LEASE_DOCUMENT_CATEGORIES,
  getActionsForState,
  type LeaseAction,
} from '../constants/lease-states';
import { useLeaseSettings } from '../hooks/useLeaseSettings';
import { LeaseService } from '../services/lease-service';
import { LeaseAccrualPeriodsTable } from '../components/LeaseAccrualPeriodsTable';
import { LeaseActivityTimeline } from '../components/LeaseActivityTimeline';
import { LeaseDetailKpiCards } from '../components/LeaseDetailKpiCards';
import { LeasePartiesPanel } from '../components/LeasePartiesPanel';
import { LeaseStateBadge } from '../components/LeaseStateBadge';
import { LeaseTransitionActions } from '../components/LeaseTransitionActions';
import { useLease, type LeaseTransitionBody } from '../hooks/useLease';
import { useAccrualPeriodActions } from '../hooks/useAccrualPeriodActions';
import { LeaseDraftCompletion, needsDraftCompletion } from '../components/LeaseDraftCompletion';
import { useLeaseDocuments, type LeaseDocument } from '../hooks/useLeaseDocuments';
import { useLeaseProperty } from '../hooks/useLeaseProperty';
import {
  buildActivity,
  currentCompetences,
  formatAccrualPeriod,
  formatCents,
  formatIsoDate,
  dueDayOfMonth,
  resolveSituation,
} from '../utils/lease-detail-derivations';
import type { Lease, LeaseExpectation } from '../types/lease.types';

const TABS = ['accrualPeriods', 'vistorias', 'documentos', 'parties'] as const;
type LeaseTab = (typeof TABS)[number];

/**
 * Lease detail, replicating the approved prototype: header (tenant, state,
 * property, vigência, origin deal) → action row → four KPI cards → a
 * two-column body with the tabbed panels on the left and the lease's
 * activity on the right.
 *
 * Three things the prototype drew are deliberately absent, each because
 * the API has nothing behind them:
 *
 * - "Registrar recebimento": the rent-receipt service has no HTTP route.
 * - The per-row "Confirmar": nothing transitions an expectation out of
 *   `awaiting_confirmation`.
 * - The `LOC-2026-0184` contract chip and "vigente até <date>": the lease
 *   model stores neither a contract code nor an end date.
 *
 * @returns The lease detail page.
 */
export function LeaseDetailPage() {
  const { agency_id: agencyId = '', lease_id: leaseId = '' } = useParams<{
    agency_id: string;
    lease_id: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation('features/leases');
  const [activeTab, setActiveTab] = useState<LeaseTab>('accrualPeriods');

  const { isLoading, isError, lease, expectations, applyTransition, refresh } = useLease(
    agencyId, leaseId,
  );
  const documents = useLeaseDocuments(agencyId, leaseId);
  const accrualActions = useAccrualPeriodActions(agencyId, leaseId);
  const vistorias = useVistoryData(agencyId, leaseId);

  const activity = useMemo(
    () => buildActivity({
      activatedAt: lease?.activated_at ?? '',
      originDealId: lease?.origin_deal_id ?? '',
      expectations,
      documentCount: documents.documents.length,
      closure: lease?.closure,
      suspensionPeriods: lease?.suspension_periods,
    }),
    [
      lease?.activated_at,
      lease?.origin_deal_id,
      lease?.closure,
      lease?.suspension_periods,
      expectations,
      documents.documents.length,
    ],
  );

  const counts: Record<LeaseTab, number> = {
    accrualPeriods: expectations.length,
    vistorias: vistorias.vistories.length,
    documentos: documents.documents.length,
    parties: (lease?.tenant_client_id ? 1 : 0) + (lease?.landlords.length ?? 0),
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-(--surface-primary) p-(--space-5)">
      <div className="flex items-center gap-(--space-2) mb-(--space-4)">
        <UnstyledButton
          type="button"
          data-testid="lease-detail-breadcrumb"
          onClick={() => navigate(`/app/agencies/${agencyId}/leases`)}
          className="flex items-center gap-(--space-1) px-(--space-2) py-(--space-1) bg-transparent border-none text-(--text-tertiary) text-(length:--text-meta) cursor-pointer hover:text-(--text-primary) hover:bg-(--surface-hover) rounded-(--radius-sm)"
        >
          <ChevronLeftIcon width={12} height={12} />
          {t('pageTitle')}
        </UnstyledButton>
      </div>

      <DataState
        isLoading={isLoading}
        isError={isError}
        isEmpty={false}
        loadingFallback={<DashboardCardsSkeleton cards={4} />}
        errorState={<DataStateError message={t('loadError')} onRetry={refresh} />}
      >
        {lease && (
          <div className="flex w-full grow flex-col gap-(--space-4)">
            <LeaseDetailHeader
              lease={lease}
              agencyId={agencyId}
              expectations={expectations}
              onApply={applyTransition}
              onNewVistoria={() => setActiveTab('vistorias')}
              onGoToDocuments={() => setActiveTab('documentos')}
            />

            {needsDraftCompletion(lease) && (
              <LeaseDraftCompletion key={lease.id} lease={lease} agencyId={agencyId} />
            )}

            <LeaseDetailKpiCards
              lease={lease}
              expectations={expectations}
              vistorias={vistorias.vistories}
              isVistoriasLoading={vistorias.isLoading}
              isVistoriasError={vistorias.isError}
            />

            {/* The grid takes the height left over below the header and the
              * figures, and the active tab panel takes what is left inside
              * the left column — otherwise a sparse lease left a dead band
              * of page under two columns that ended at different heights. */}
            <div className="grid grow grid-cols-1 items-start gap-(--space-4) lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:items-stretch">
              <div className="flex min-w-0 flex-col gap-(--space-3) [&>*:last-child]:grow [&>*:last-child]:shrink-0">
                <div
                  role="tablist"
                  className="flex flex-nowrap gap-0 px-(--space-3) border-b border-(--border-subtle) overflow-x-auto scrollbar-none"
                >
                  {TABS.map((tab) => (
                    <TabButton
                      key={tab}
                      tab={tab}
                      count={counts[tab]}
                      isActive={activeTab === tab}
                      onSelect={() => setActiveTab(tab)}
                    />
                  ))}
                </div>

                {activeTab === 'accrualPeriods' && (
                  <LeaseAccrualPeriodsTable
                    expectations={expectations}
                    dueDatePolicy={lease.due_date_policy}
                    actions={accrualActions}
                  />
                )}
                {activeTab === 'vistorias' && (
                  <LeaseVistoriesPanel
                    agencyId={agencyId}
                    leaseId={leaseId}
                    propertyId={lease.property_id}
                  />
                )}
                {activeTab === 'documentos' && (
                  <LeaseDocumentsTab
                    agencyId={agencyId}
                    leaseId={leaseId}
                    documents={documents.documents}
                    isLoading={documents.isLoading}
                    isError={documents.isError}
                    onRefetch={documents.refetch}
                  />
                )}
                {activeTab === 'parties' && (
                  <LeasePartiesPanel lease={lease} agencyId={agencyId} />
                )}
              </div>

              <div className="flex flex-col gap-(--space-3) min-w-0 lg:sticky lg:top-(--space-5)">
                <ContractSummary lease={lease} />
                <LeaseActivityTimeline entries={activity} />
              </div>
            </div>
          </div>
        )}
      </DataState>
    </div>
  );
}

/**
 * The header block: who rents, in what state, which property, since when,
 * and the row of actions.
 */
function LeaseDetailHeader({
  lease,
  agencyId,
  expectations,
  onApply,
  onNewVistoria,
  onGoToDocuments,
  today = new Date(),
}: {
  lease: Lease;
  agencyId: string;
  expectations: LeaseExpectation[];
  onApply: (action: LeaseAction, body?: LeaseTransitionBody) => Promise<void>;
  onNewVistoria: () => void;
  onGoToDocuments: () => void;
  today?: Date;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation('features/leases');
  const property = useLeaseProperty(agencyId, lease.property_id);
  const hasPrimaryTransition = getActionsForState(lease.state)
    .some((action) => action === 'activate' || action === 'resume');

  const subtitle = [
    property.label || lease.property_id,
    lease.activated_at
      ? t('detail.vigenciaSince', { date: formatIsoDate(lease.activated_at) })
      : t('detail.notActivated'),
  ].join(' · ');

  return (
    <div className="overflow-hidden rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-card)">
      <div className="flex flex-wrap items-start justify-between gap-(--space-5) px-(--space-6) pt-(--space-5) pb-(--space-4)">
        <div className="flex min-w-0 flex-col gap-(--space-1-5)">
          <div className="flex flex-wrap items-center gap-(--space-2-5)">
            <Title
              order={1}
              className="text-(length:--text-app-title) [font-weight:var(--font-semibold)] tracking-(--tracking-app-title) text-(--text-primary) m-0 leading-[1.15]"
            >
              {lease.tenant_name || t('detail.noTenant')}
            </Title>
            <LeaseStateBadge state={lease.state} />
          </div>

          <Text
            component="span"
            data-testid="lease-detail-subtitle"
            className="text-(length:--text-body-sm) text-(--text-secondary)"
          >
            {subtitle}
          </Text>

          {lease.origin_deal_id && (
            <UnstyledButton
              type="button"
              data-testid="lease-origin-deal-link"
              onClick={() => navigate(`/app/agencies/${agencyId}/deals/${lease.origin_deal_id}`)}
              className="w-fit px-0 py-0 bg-transparent border-none text-(length:--text-meta) text-(--seed-base) cursor-pointer hover:underline"
            >
              {t('detail.originDeal')}
            </UnstyledButton>
          )}
        </div>
      </div>

      <LeaseStatusBand
        lease={lease}
        expectations={expectations}
        today={today}
        actions={
          <>
            {/* Solid only when the state has no transition of its own to
              * lead with. A draft offers "Registrar assinatura" and a
              * suspended lease "Retomar"; those are the primary move there,
              * and two solid buttons side by side name no priority at all. */}
            <Button
              type="button"
              size="custom"
              variant="none"
              className={hasPrimaryTransition ? DETAIL_ACTION_SECONDARY : DETAIL_ACTION_PRIMARY}
              onClick={onNewVistoria}
              data-testid="lease-new-vistoria"
            >
              {t('detail.newVistoria')}
            </Button>
            {/* The upload lives inside the Documentos tab and stays there.
              * This only takes the reader to it, the same way the sale
              * screen's two quiet buttons take theirs to a tab. */}
            <Button
              type="button"
              size="custom"
              variant="none"
              className={DETAIL_ACTION_SECONDARY}
              onClick={onGoToDocuments}
              data-testid="lease-send-document"
            >
              {t('detail.sendDocument')}
            </Button>
            <div
              aria-hidden="true"
              className="mx-(--space-1) hidden h-6 w-px self-center bg-(--border-subtle) sm:block"
            />
            <LeaseTransitionActions state={lease.state} onApply={onApply} />
          </>
        }
      />
    </div>
  );
}

/**
 * The header's second band: which competência is open, and what can be done
 * to the lease.
 *
 * There is no progress bar here on purpose. The seven lease states are a
 * state graph, not a funnel — a suspended lease has not advanced past an
 * active one — so a strip filling left to right would be asserting an order
 * that does not exist. What does move month by month is the rent, so the
 * band leads with the current competência and the one before it.
 *
 * @param props.lease - The lease, for its due-date policy.
 * @param props.expectations - Every competência the lease has.
 * @param props.today - Reference date, injected so tests do not chase the clock.
 * @param props.actions - The action row.
 * @returns The status band.
 */
export function LeaseStatusBand({
  lease,
  expectations,
  today,
  actions,
}: {
  lease: Lease;
  expectations: LeaseExpectation[];
  today: Date;
  actions?: ReactNode;
}) {
  const { t } = useTranslation('features/leases');
  const { currentPeriod, current, previousPeriod, previous } = currentCompetences(
    expectations,
    today,
  );

  const currentSituation = current ? resolveSituation(current) : null;
  const headline = t('detail.competence.headline', {
    period: formatAccrualPeriod(currentPeriod),
    situation: currentSituation
      ? t(`accrualPeriods.situation.${currentSituation.tone}`, {
        count: currentSituation.daysOverdue,
      })
      : t('kpiDetail.notGenerated'),
  });

  const due = current ? t('detail.competence.due', { date: formatIsoDate(current.due_date) }) : '';

  const previousLine = previous
    ? t('detail.competence.previous', {
      period: formatAccrualPeriod(previousPeriod),
      situation: t(`accrualPeriods.situation.${resolveSituation(previous).tone}`, {
        count: resolveSituation(previous).daysOverdue,
      }),
    })
    : t('detail.competence.previousMissing', { period: formatAccrualPeriod(previousPeriod) });

  return (
    <div
      data-testid="lease-status-band"
      // The page ground, like the property detail's PriceStrip and the
      // attendance's band. `--surface-tertiary` is a well tier, so the
      // band read as a tray bolted under the header rather than part of it.
      className="flex flex-wrap items-center gap-(--space-4) border-t border-(--border-subtle) bg-(--surface-primary) px-(--space-6) py-(--space-3-5)"
    >
      <div className="flex min-w-[250px] flex-1 flex-col gap-(--space-1)">
        <div className="flex flex-wrap items-baseline gap-(--space-2)">
          <Text
            component="span"
            className="text-(length:--text-app-section) [font-weight:var(--font-semibold)] leading-tight"
            style={{ color: currentSituation ? SITUATION_TONE_COLOR[currentSituation.tone] : 'var(--text-primary)' }}
          >
            {headline}
          </Text>
          {due && (
            <Text component="span" className="text-(length:--text-meta) text-(--text-tertiary-soft)">
              {due}
            </Text>
          )}
        </div>
        <Text component="span" className="text-(length:--text-meta) text-(--text-secondary)">
          {previousLine}
        </Text>
      </div>

      {actions && <div className="ml-auto flex flex-wrap items-center justify-end gap-(--space-2)">{actions}</div>}
    </div>
  );
}

/** Tone colours for the competência headline, matching the table's badges. */
const SITUATION_TONE_COLOR: Record<string, string> = {
  settled: 'var(--status-success-text)',
  awaiting: 'var(--status-warning-text)',
  overdue: 'var(--status-error-text)',
  open: 'var(--text-secondary)',
  writtenOff: 'var(--text-secondary)',
  cancelled: 'var(--text-secondary)',
};

/**
 * One tab in the tablist, with the count badge the prototype shows.
 */
function TabButton({
  tab,
  count,
  isActive,
  onSelect,
}: {
  tab: LeaseTab;
  count: number;
  isActive: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation('features/leases');

  return (
    <UnstyledButton
      type="button"
      role="tab"
      aria-selected={isActive}
      data-testid={`lease-tab-${tab}`}
      onClick={onSelect}
      className={cn(
        // gap-0 and the roomier padding match the property, client and
        // attendance tabs: one straight rule across the strip instead of
        // segments, and an active tab in seed rather than ink.
        'shrink-0 whitespace-nowrap flex items-center gap-(--space-2) rounded-none',
        'px-(--space-5) py-(--space-3) text-(length:--text-body-sm)',
        'bg-transparent border-0 border-b-2 border-solid cursor-pointer [font-family:var(--font-family)]',
        isActive
          ? '[font-weight:var(--font-semibold)] text-(--seed-base) border-b-(--seed-base)'
          : '[font-weight:var(--font-medium)] text-(--text-secondary) border-b-transparent hover:text-(--text-primary)',
      )}
    >
      <Text component="span">{t(`detail.tabs.${tab}`)}</Text>
      <Text
        component="span"
        className="rounded-full px-(--space-2) py-px text-(length:--text-micro)"
        style={isActive
          ? {
            background: 'color-mix(in srgb, var(--seed-base) 12%, transparent)',
            color: 'var(--seed-base)',
          }
          : { background: 'var(--surface-tertiary)', color: 'var(--text-tertiary)' }}
      >
        {count}
      </Text>
    </UnstyledButton>
  );
}

/**
 * The contract facts that no longer have a tab of their own.
 *
 * The prototype's four tabs left no home for the rent composition, the
 * responsible broker or the billing locale, all of which the previous
 * screen showed. Dropping them would be a regression, so they sit here in
 * the right column rather than disappearing.
 */
function ContractSummary({ lease }: { lease: Lease }) {
  const { t } = useTranslation('features/leases');
  const dueDay = dueDayOfMonth(lease.due_date_policy);

  // The monthly rent leads the figures strip above, so repeating it here
  // would print the same number twice on one screen. The due day, which
  // previously only appeared in the competências footer, takes its place.
  const rows: Array<{ label: string; value: string }> = [
    {
      label: t('detail.firstAccrualPeriod'),
      value: formatAccrualPeriod(lease.first_accrual_period) || '—',
    },
    {
      label: t('accrualPeriods.columns.dueDate'),
      value: dueDay === null ? '—' : t('detail.dueDayValue', { day: dueDay }),
    },
    { label: t('detail.broker'), value: lease.responsible_broker_email || '—' },
    { label: t('detail.billingLocale'), value: lease.billing_locale || '—' },
  ];

  return (
    <SectionCard title={t('detail.contract')}>
      <div className="flex flex-col" data-testid="lease-contract-summary">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between gap-(--space-3) py-(--space-1-5)"
          >
            <Text component="span" className="text-(length:--text-meta) text-(--text-tertiary)">
              {row.label}
            </Text>
            <Text
              component="span"
              className="text-(length:--text-meta) [font-weight:var(--font-medium)] text-(--text-primary) text-right truncate"
            >
              {row.value}
            </Text>
          </div>
        ))}

        {lease.charge_schedule.length > 1 && (
          <div className="flex flex-col gap-(--space-1) mt-(--space-2) pt-(--space-2) border-t border-(--border-subtle)">
            <Text component="span" className="text-(length:--text-micro) uppercase tracking-(--tracking-wider) text-(--text-tertiary)">
              {t('detail.chargeSchedule')}
            </Text>
            {lease.charge_schedule.map((line, index) => (
              <div
                key={`${line.kind}-${index}`}
                className="flex items-center justify-between gap-(--space-3)"
              >
                <Text component="span" className="text-(length:--text-meta) text-(--text-secondary)">
                  {line.kind}
                </Text>
                <Text component="span" className="text-(length:--text-meta) text-(--text-primary) [font-variant-numeric:tabular-nums]">
                  {line.amount_cents !== null
                    ? formatCents(line.amount_cents)
                    : `${(line.percent_bps ?? 0) / 100}%`}
                </Text>
              </div>
            ))}
          </div>
        )}
      </div>
    </SectionCard>
  );
}

/**
 * The lease's own papers: the signed contract, inspections, warranty.
 *
 * Reuses the sale's documents panel -- the paperwork behaves identically
 * and only the category vocabulary differs, so it is passed in. The list
 * itself is fetched by the page (the tab badge needs the count too) and
 * handed down.
 *
 * @param props.agencyId - The owning agency.
 * @param props.leaseId - The lease.
 * @param props.documents - The already-loaded document list.
 * @param props.isLoading - Whether that read is in flight.
 * @param props.isError - Whether that read failed.
 * @param props.onRefetch - Re-reads the list after a mutation.
 * @returns The documents tab body.
 */
function LeaseDocumentsTab({
  agencyId,
  leaseId,
  documents,
  isLoading,
  isError,
  onRefetch,
}: {
  agencyId: string;
  leaseId: string;
  documents: LeaseDocument[];
  isLoading: boolean;
  isError: boolean;
  onRefetch: () => Promise<unknown>;
}) {
  // The vocabulary is the agency's own; the five built-in ids stay
  // translated by i18n, and only the ones it added carry their own label.
  const { documentCategories } = useLeaseSettings(agencyId);
  const categoryIds = useMemo(
    () => documentCategories.map((c) => c.id),
    [documentCategories],
  );
  const categoryLabels = useMemo(
    () => Object.fromEntries(
      documentCategories
        .filter((c) => !BUILT_IN_DOCUMENT_CATEGORY_IDS.has(c.id))
        .map((c) => [c.id, c.label]),
    ),
    [documentCategories],
  );

  async function upload(file: File, category: string, label?: string) {
    const link = await LeaseService.createDocumentUploadLink(agencyId, leaseId, {
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
      category,
      ...(label ? { label } : {}),
    });
    const form = new FormData();
    Object.entries(link.upload_url.fields ?? {}).forEach(([k, v]) => form.append(k, v));
    form.append('file', file);
    await fetch(link.upload_url.url, { method: 'POST', body: form });
    await onRefetch();
  }

  return (
    <EntityDocumentsPanel
      documents={documents}
      categories={categoryIds.length ? categoryIds : LEASE_DOCUMENT_CATEGORIES}
      categoryLabels={categoryLabels}
      namespace="features/leases"
      isLoading={isLoading}
      isError={isError}
      onUpload={upload}
      onOpen={async (documentId) => {
        const { url } = await LeaseService.getDocumentViewLink(agencyId, leaseId, documentId);
        window.open(url, '_blank', 'noopener');
      }}
      onDelete={async (documentId) => {
        await LeaseService.deleteDocument(agencyId, leaseId, documentId);
        await onRefetch();
      }}
      onRetry={() => void onRefetch()}
    />
  );
}

export default LeaseDetailPage;
